// Unified pixel-themed icon system for the entire game
// All icons are designed to match the game's aesthetic with consistent styling

interface PixelIcon {
  viewBox: string;
  path: string;
}

const pixelIcons: Record<string, PixelIcon> = {
  // Navigation & UI Icons
  play: {
    viewBox: '0 0 24 24',
    path: `<path d="M6 4v16l12-8z" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linejoin="miter"/>`,
  },

  close: {
    viewBox: '0 0 24 24',
    path: `<path d="M6 6h2v2H6V6zm2 2h2v2H8V8zm2 2h2v2h-2v-2zm2 2h2v2h-2v-2zm2 2h2v2h-2v-2z" fill="currentColor"/>
          <path d="M16 6h2v2h-2V6zm-2 2h2v2h-2V8zm-2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm-2 2h2v2H8v-2z" fill="currentColor"/>`,
  },

  home: {
    viewBox: '0 0 24 24',
    path: `<path d="M10 2h4v2h-4V2zm-2 2h2v2H8V4zm8 0h2v2h-2V4zm-8 2H6v2h2V6zm8 0h2v2h-2V6z" fill="currentColor"/>
          <rect x="4" y="8" width="16" height="12" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="10" y="14" width="4" height="6" fill="currentColor"/>`,
  },

  settings: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="10" y="2" width="4" height="4" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="4" fill="currentColor"/>
          <rect x="2" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="18" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="4" y="4" width="4" height="4" fill="currentColor" opacity="0.7"/>
          <rect x="16" y="4" width="4" height="4" fill="currentColor" opacity="0.7"/>
          <rect x="4" y="16" width="4" height="4" fill="currentColor" opacity="0.7"/>
          <rect x="16" y="16" width="4" height="4" fill="currentColor" opacity="0.7"/>`,
  },

  // Game Action Icons
  restart: {
    viewBox: '0 0 24 24',
    path: `<path d="M4 10h2v4H4v-4zm2-2h2v2H6V8zm2-2h2v2H8V6zm2-2h2v2h-2V4zm2 0h2v2h-2V4zm2 2h2v2h-2V6zm2 2h2v2h-2V8zm2 2h2v4h-2v-4zm-2 4h2v2h-2v-2zm-2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm-2 0h2v2h-2v-2zm-2-2h2v2H8v-2zm-2-2h2v2H6v-2z" fill="none" stroke="currentColor" stroke-width="0"/>
          <rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="2" height="2" fill="currentColor"/>
          <path d="M12 2h2v2h2v2h-4V2z" fill="currentColor"/>`,
  },

  fullscreen: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="4" width="2" height="6" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="20" y="4" width="2" height="6" fill="currentColor"/>
          <rect x="2" y="14" width="2" height="6" fill="currentColor"/>
          <rect x="2" y="20" width="8" height="2" fill="currentColor"/>
          <rect x="14" y="20" width="8" height="2" fill="currentColor"/>
          <rect x="20" y="14" width="2" height="6" fill="currentColor"/>`,
  },

  portal: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="14" y="6" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="14" y="16" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="6" y="14" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="16" y="14" width="2" height="2" fill="currentColor" opacity="0.85"/>
          <rect x="8" y="8" width="8" height="8" fill="currentColor" opacity="0.25"/>
          <rect x="10" y="10" width="4" height="4" fill="white" opacity="0.55"/>`,
  },

  // Shop & Currency Icons
  shop: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="8" width="16" height="12" fill="none" stroke="currentColor" stroke-width="2"/>
          <path d="M4 8h2V6h2V4h2V2h4v2h2v2h2v2h2" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="miter"/>
          <rect x="8" y="12" width="2" height="8" fill="currentColor"/>
          <rect x="14" y="12" width="2" height="8" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="4" fill="currentColor"/>`,
  },

  coin: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="18" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="currentColor"/>`,
  },

  coins: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="8" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="11" y="11" width="2" height="2" fill="white"/>`,
  },

  gem: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="white" opacity="0.6"/>
          <rect x="9" y="10" width="2" height="2" fill="white" opacity="0.4"/>
          <rect x="13" y="10" width="2" height="2" fill="white" opacity="0.4"/>`,
  },

  gems: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="8" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="white" opacity="0.8"/>`,
  },

  life: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="4" height="4" fill="currentColor"/>
          <rect x="14" y="4" width="4" height="4" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="2" y="8" width="2" height="4" fill="currentColor"/>
          <rect x="20" y="8" width="2" height="4" fill="currentColor"/>
          <rect x="4" y="12" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="white" opacity="0.8"/>
          <rect x="12" y="8" width="2" height="2" fill="white" opacity="0.8"/>`,
  },

  heart: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="6" width="6" height="4" fill="currentColor"/>
          <rect x="14" y="6" width="6" height="4" fill="currentColor"/>
          <rect x="2" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="16" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="2" fill="currentColor"/>`,
  },

  gift: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="8" width="16" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="10" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="10" fill="white" opacity="0.3"/>
          <rect x="10" y="2" width="4" height="6" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="14" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="2" y="8" width="20" height="4" fill="currentColor"/>
          <rect x="11" y="8" width="2" height="4" fill="white" opacity="0.4"/>`,
  },

  fire: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="2" fill="currentColor"/>
          <rect x="16" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="2" fill="currentColor"/>
          <rect x="7" y="16" width="10" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="12" width="2" height="4" fill="#f39c12" opacity="0.8"/>
          <rect x="9" y="14" width="2" height="2" fill="#e74c3c" opacity="0.6"/>
          <rect x="13" y="14" width="2" height="2" fill="#e74c3c" opacity="0.6"/>`,
  },

  score: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="2" width="2" height="6" fill="currentColor"/>
          <rect x="9" y="6" width="6" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="16" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="7" y="12" width="10" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="white" opacity="0.8"/>`,
  },

  // Level & Progress Icons
  star: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="2" width="2" height="4" fill="currentColor"/>
          <rect x="9" y="6" width="6" height="2" fill="currentColor"/>
          <rect x="5" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="17" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="3" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="17" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="7" y="12" width="10" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>`,
  },

  lock: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="12" width="12" height="8" fill="currentColor" stroke="currentColor" stroke-width="2"/>
          <rect x="8" y="8" width="2" height="4" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="14" y="8" width="2" height="4" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="8" y="6" width="8" height="2" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="11" y="15" width="2" height="2" fill="white"/>`,
  },

  levelSelect: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="2" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="14" width="8" height="8" fill="currentColor" opacity="0.5"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  // Audio & Settings Icons
  sound: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="2" height="16" fill="currentColor"/>
          <rect x="14" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="20" y="4" width="2" height="16" fill="currentColor"/>`,
  },

  music: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="2" width="2" height="14" fill="currentColor"/>
          <rect x="8" y="2" width="6" height="2" fill="currentColor"/>
          <rect x="14" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="2" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="2" height="4" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="22" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="10" width="2" height="2" fill="currentColor"/>`,
  },

  vibration: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="16" fill="currentColor" stroke="currentColor" stroke-width="2"/>
          <rect x="4" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="18" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="22" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="white"/>
          <rect x="10" y="12" width="4" height="2" fill="white"/>
          <rect x="10" y="16" width="4" height="2" fill="white"/>`,
  },

  // Powerup & Item Icons
  speedBoost: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="10" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="12" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  shield: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="16" height="8" fill="currentColor"/>
          <rect x="6" y="16" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="6" fill="white"/>
          <rect x="9" y="12" width="6" height="2" fill="white"/>`,
  },

  hint: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="16" width="2" height="1" fill="currentColor"/>`,
  },

  invincibility: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="11" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="12" width="6" height="2" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="16" width="4" height="1" fill="currentColor"/>`,
  },

  // Navigation Icons (missing from config)
  levels: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="2" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="4" y="4" width="4" height="4" fill="white" opacity="0.8"/>
          <rect x="16" y="4" width="4" height="4" fill="white" opacity="0.8"/>
          <rect x="4" y="16" width="4" height="4" fill="white" opacity="0.8"/>
          <rect x="16" y="16" width="4" height="4" fill="white" opacity="0.8"/>`,
  },

  user: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="6" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="white"/>
          <rect x="4" y="12" width="16" height="8" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="4" fill="white" opacity="0.8"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>`,
  },

  continue: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="14" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="4" width="2" height="16" fill="currentColor"/>`,
  },

  pause: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="4" height="16" fill="currentColor"/>
          <rect x="14" y="4" width="4" height="16" fill="currentColor"/>`,
  },

  magnet: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="14" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="8" fill="white" opacity="0.12"/>`,
  },

  skip: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="7" width="2" height="10" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="10" y="9" width="2" height="6" fill="currentColor"/>
          <rect x="12" y="10" width="2" height="4" fill="currentColor"/>

          <rect x="12" y="7" width="2" height="10" fill="currentColor" opacity="0.9"/>
          <rect x="14" y="8" width="2" height="8" fill="currentColor" opacity="0.9"/>
          <rect x="16" y="9" width="2" height="6" fill="currentColor" opacity="0.9"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor" opacity="0.9"/>

          <rect x="20" y="6" width="2" height="12" fill="currentColor"/>`,
  },

  undo: {
    viewBox: '0 0 24 24',
    path: `<rect x="7" y="10" width="10" height="2" fill="currentColor"/>
          <rect x="5" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="8" width="2" height="10" fill="currentColor"/>
          <rect x="13" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="4" width="2" height="2" fill="currentColor" opacity="0.65"/>`,
  },

  worlds: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="6" width="6" height="12" fill="currentColor" opacity="0.85"/>
          <rect x="10" y="4" width="6" height="16" fill="currentColor"/>
          <rect x="16" y="6" width="4" height="12" fill="currentColor" opacity="0.85"/>
          <rect x="6" y="8" width="2" height="2" fill="white" opacity="0.55"/>
          <rect x="12" y="8" width="2" height="2" fill="white" opacity="0.45"/>
          <rect x="14" y="12" width="2" height="2" fill="white" opacity="0.45"/>
          <rect x="12" y="16" width="2" height="2" fill="white" opacity="0.45"/>
          <rect x="18" y="10" width="1" height="1" fill="white" opacity="0.55"/>
          <rect x="18" y="14" width="1" height="1" fill="white" opacity="0.55"/>`,
  },

  upgrades: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="4" width="2" height="12" fill="currentColor"/>
          <rect x="9" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="16" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>`,
  },

  // Additional icons for completeness
  lightning: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="10" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="12" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  'arrow-left': {
    viewBox: '0 0 24 24',
    path: `<rect x="12" y="10" width="8" height="4" fill="currentColor"/>
          <rect x="8" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="4" fill="currentColor"/>`,
  },

  gamepad: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="8" fill="currentColor"/>
          <rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="2" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="white"/>
          <rect x="14" y="10" width="2" height="2" fill="white"/>
          <rect x="10" y="11" width="4" height="2" fill="white"/>
          <rect x="11" y="9" width="2" height="4" fill="white"/>`,
  },

  eye: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="10" width="12" height="4" fill="currentColor"/>
          <rect x="4" y="11" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="11" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="11" width="4" height="2" fill="white"/>
          <rect x="11" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  // Additional icons needed for modals
  success: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>`,
  },

  retry: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="2" height="2" fill="currentColor"/>
          <path d="M12 2h2v2h2v2h-4V2z" fill="currentColor"/>`,
  },

  steps: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="12" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  clock: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="11" y="8" width="2" height="6" fill="currentColor"/>
          <rect x="13" y="11" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="6" width="2" height="2" fill="currentColor"/>`,
  },

  speed: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  // Additional icons for settings menu
  effects: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="16" height="8" fill="currentColor"/>
          <rect x="6" y="16" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="6" fill="white"/>
          <rect x="9" y="12" width="6" height="2" fill="white"/>`,
  },

  // Character customization icons
  mask: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="8" fill="currentColor"/>
          <rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="white"/>
          <rect x="14" y="10" width="2" height="2" fill="white"/>
          <rect x="10" y="13" width="4" height="2" fill="white"/>`,
  },

  crown: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="12" width="16" height="6" fill="currentColor"/>
          <rect x="6" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="4" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="#f39c12"/>
          <rect x="7" y="14" width="2" height="2" fill="#f39c12"/>
          <rect x="15" y="14" width="2" height="2" fill="#f39c12"/>`,
  },

  glasses: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="10" width="6" height="4" fill="currentColor"/>
          <rect x="14" y="10" width="6" height="4" fill="currentColor"/>
          <rect x="10" y="11" width="4" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="2" height="2" fill="white"/>
          <rect x="16" y="12" width="2" height="2" fill="white"/>
          <rect x="2" y="11" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  hair: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="8" width="2" height="4" fill="currentColor"/>
          <rect x="20" y="8" width="2" height="4" fill="currentColor"/>`,
  },

  hand: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="2" height="8" fill="currentColor"/>
          <rect x="10" y="2" width="2" height="10" fill="currentColor"/>
          <rect x="12" y="4" width="2" height="8" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="6" fill="currentColor"/>
          <rect x="6" y="12" width="12" height="6" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>`,
  },

  backpack: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="12" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="8" height="6" fill="white" opacity="0.3"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="2" height="6" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="6" fill="currentColor"/>`,
  },

  target: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="8" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="white"/>
          <rect x="11" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  trophy: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="8" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="4" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="#f39c12"/>
          <rect x="11" y="6" width="2" height="2" fill="#f39c12"/>`,
  },

  chart: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="18" width="2" height="4" fill="currentColor"/>
          <rect x="8" y="14" width="2" height="8" fill="currentColor"/>
          <rect x="12" y="10" width="2" height="12" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="16" fill="currentColor"/>
          <rect x="20" y="4" width="2" height="18" fill="currentColor"/>
          <rect x="2" y="20" width="20" height="2" fill="currentColor"/>`,
  },

  strength: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="8" width="8" height="8" fill="currentColor"/>
          <rect x="6" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="4" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="white"/>
          <rect x="11" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  loading: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="6" fill="currentColor"/>
          <rect x="16" y="4" width="4" height="4" fill="currentColor" opacity="0.8"/>
          <rect x="18" y="10" width="4" height="4" fill="currentColor" opacity="0.6"/>
          <rect x="16" y="16" width="4" height="4" fill="currentColor" opacity="0.4"/>
          <rect x="10" y="18" width="4" height="4" fill="currentColor" opacity="0.2"/>
          <rect x="4" y="16" width="4" height="4" fill="currentColor" opacity="0.1"/>
          <rect x="2" y="10" width="4" height="4" fill="currentColor" opacity="0.05"/>
          <rect x="4" y="4" width="4" height="4" fill="currentColor" opacity="0.9"/>`,
  },

  wifi: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="12" width="20" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="16" width="4" height="2" fill="currentColor"/>`,
  },

  mobile: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="2" width="12" height="20" fill="currentColor"/>
          <rect x="8" y="4" width="8" height="14" fill="white"/>
          <rect x="10" y="19" width="4" height="2" fill="white"/>
          <rect x="11" y="6" width="2" height="2" fill="currentColor"/>`,
  },

  grid: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="2" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="14" width="8" height="8" fill="currentColor"/>`,
  },

  search: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="8" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="8" fill="currentColor"/>
          <rect x="6" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="14" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  error: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="6" fill="currentColor"/>
          <rect x="11" y="16" width="2" height="2" fill="currentColor"/>`,
  },

  construction: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="18" width="20" height="4" fill="currentColor"/>
          <rect x="4" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="8" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="white"/>
          <rect x="6" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="10" fill="currentColor"/>`,
  },

  palette: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="14" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="#e74c3c"/>
          <rect x="12" y="8" width="2" height="2" fill="#3498db"/>
          <rect x="16" y="8" width="2" height="2" fill="#f39c12"/>
          <rect x="8" y="12" width="2" height="2" fill="#27ae60"/>
          <rect x="12" y="12" width="2" height="2" fill="#9b59b6"/>
          <rect x="16" y="12" width="2" height="2" fill="#e67e22"/>`,
  },

  // Enemy Icons
  enemyBeetle: {
    viewBox: '0 0 24 24',
    path: `<rect x="9" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="4" fill="currentColor"/>
          <rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="12" width="12" height="4" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="16" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="white" opacity="0.6"/>`,
  },

  enemyDrone: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="4" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="20" width="2" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="3" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="19" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="2" fill="white" opacity="0.5"/>`,
  },

  enemyMite: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="8" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="18" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="4" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="2" height="2" fill="white" opacity="0.6"/>
          <rect x="12" y="10" width="2" height="2" fill="white" opacity="0.6"/>`,
  },

  enemyHound: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="4" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="11" width="2" height="2" fill="white" opacity="0.7"/>
          <rect x="14" y="11" width="2" height="2" fill="white" opacity="0.7"/>
          <rect x="10" y="14" width="4" height="2" fill="white" opacity="0.4"/>`,
  },

  enemyMirror: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="5" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="17" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="3" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="19" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="5" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="17" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="20" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="8" width="6" height="8" fill="currentColor" opacity="0.35"/>
          <rect x="10" y="10" width="2" height="4" fill="white" opacity="0.5"/>
          <rect x="12" y="10" width="2" height="4" fill="white" opacity="0.25"/>`,
  },

  chevronDown: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="14" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="8" width="2" height="2" fill="currentColor"/>`,
  },
};

// Helper function to generate consistent SVG markup
export function getPixelIcon(name: string, size: number = 24, color: string = 'currentColor'): string {
  const icon = pixelIcons[name];
  if (!icon) {
    console.warn(`Icon "${name}" not found in pixel icon system`);
    return '';
  }

  return `<svg 
    class="pixel-icon pixel-icon-${name}" 
    width="${size}" 
    height="${size}" 
    viewBox="${icon.viewBox}" 
    fill="none"
    style="color: ${color};"
  >
    ${icon.path}
  </svg>`;
}

// Export for use throughout the app
export { pixelIcons };

// Also make it available globally for backward compatibility
declare global {
  interface Window {
    getPixelIcon: typeof getPixelIcon;
  }
}

if (typeof window !== 'undefined') {
  window.getPixelIcon = getPixelIcon;
}
